// Machine Learning Models for Trading
export class TradingML {
  constructor() {
    this.models = {
      pricePrediction: null,
      riskAssessment: null,
      sentimentAnalysis: null
    };
    this.trainingData = [];
    this.features = [];
  }

  // Simple Linear Regression for price prediction
  linearRegression(features, targets) {
    const n = features.length;
    if (n === 0) return null;

    // Calculate means
    const xMean = features.reduce((sum, x) => sum + x, 0) / n;
    const yMean = targets.reduce((sum, y) => sum + y, 0) / n;

    // Calculate slope and intercept
    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < n; i++) {
      numerator += (features[i] - xMean) * (targets[i] - yMean);
      denominator += Math.pow(features[i] - xMean, 2);
    }

    const slope = denominator === 0 ? 0 : numerator / denominator;
    const intercept = yMean - slope * xMean;

    return { slope, intercept };
  }

  // Predict next price using linear regression
  predictPrice(historicalPrices, lookbackPeriod = 10) {
    if (historicalPrices.length < lookbackPeriod + 1) return null;

    const features = [];
    const targets = [];

    for (let i = lookbackPeriod; i < historicalPrices.length; i++) {
      const feature = historicalPrices.slice(i - lookbackPeriod, i).reduce((sum, price) => sum + price, 0) / lookbackPeriod;
      features.push(feature);
      targets.push(historicalPrices[i]);
    }

    const model = this.linearRegression(features, targets);
    if (!model) return null;

    const recentFeature = historicalPrices.slice(-lookbackPeriod).reduce((sum, price) => sum + price, 0) / lookbackPeriod;
    return model.slope * recentFeature + model.intercept;
  }

  // Simple Moving Average Crossover Strategy
  smaCrossoverStrategy(prices, shortPeriod = 10, longPeriod = 20) {
    if (prices.length < longPeriod) return 'HOLD';

    const shortSMA = this.calculateSMA(prices, shortPeriod);
    const longSMA = this.calculateSMA(prices, longPeriod);

    if (!shortSMA || !longSMA) return 'HOLD';

    if (shortSMA > longSMA) return 'BUY';
    if (shortSMA < longSMA) return 'SELL';
    return 'HOLD';
  }

  calculateSMA(prices, period) {
    if (prices.length < period) return null;
    const sum = prices.slice(-period).reduce((a, b) => a + b, 0);
    return sum / period;
  }

  // Risk Assessment Model
  assessRisk(trade, marketData, portfolio) {
    const riskFactors = {
      volatility: this.calculateVolatility(marketData.prices || [trade.entryPrice]),
      leverage: trade.leverage || 1,
      positionSize: trade.amount || 0,
      portfolioExposure: this.calculatePortfolioExposure(portfolio),
      marketConditions: this.analyzeMarketConditions(marketData)
    };

    let riskScore = 0;

    // Volatility risk (0-40 points)
    if (riskFactors.volatility > 0.05) riskScore += 40;
    else if (riskFactors.volatility > 0.03) riskScore += 25;
    else if (riskFactors.volatility > 0.01) riskScore += 10;

    // Leverage risk (0-30 points)
    if (riskFactors.leverage > 50) riskScore += 30;
    else if (riskFactors.leverage > 20) riskScore += 20;
    else if (riskFactors.leverage > 10) riskScore += 10;

    // Position size risk (0-20 points)
    const maxPositionSize = portfolio.totalValue * 0.1; // 10% max per position
    if (riskFactors.positionSize > maxPositionSize) riskScore += 20;
    else if (riskFactors.positionSize > maxPositionSize * 0.5) riskScore += 10;

    // Portfolio exposure risk (0-10 points)
    if (riskFactors.portfolioExposure > 0.8) riskScore += 10;
    else if (riskFactors.portfolioExposure > 0.6) riskScore += 5;

    return {
      score: Math.min(riskScore, 100),
      level: riskScore > 70 ? 'HIGH' : riskScore > 40 ? 'MEDIUM' : 'LOW',
      factors: riskFactors,
      recommendations: this.generateRiskRecommendations(riskScore, riskFactors)
    };
  }

  calculateVolatility(prices) {
    if (prices.length < 2) return 0;
    
    const returns = [];
    for (let i = 1; i < prices.length; i++) {
      returns.push((prices[i] - prices[i - 1]) / prices[i - 1]);
    }
    
    const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / returns.length;
    
    return Math.sqrt(variance);
  }

  calculatePortfolioExposure(portfolio) {
    if (!portfolio || !portfolio.trades) return 0;
    
    const activeTrades = portfolio.trades.filter(t => t.status === 'ACTIVE');
    const totalExposure = activeTrades.reduce((sum, trade) => sum + (trade.amount * trade.leverage), 0);
    
    return portfolio.totalValue > 0 ? totalExposure / portfolio.totalValue : 0;
  }

  analyzeMarketConditions(marketData) {
    const conditions = {
      trend: 'NEUTRAL',
      volatility: 'NORMAL',
      momentum: 'NEUTRAL'
    };

    if (marketData.prices && marketData.prices.length > 0) {
      const prices = marketData.prices;
      const recent = prices.slice(-10);
      const older = prices.slice(-20, -10);
      
      if (recent.length > 0 && older.length > 0) {
        const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
        const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
        
        if (recentAvg > olderAvg * 1.02) conditions.trend = 'BULLISH';
        else if (recentAvg < olderAvg * 0.98) conditions.trend = 'BEARISH';
      }
    }

    return conditions;
  }

  generateRiskRecommendations(riskScore, riskFactors) {
    const recommendations = [];

    if (riskScore > 70) {
      recommendations.push('Consider reducing position size');
      recommendations.push('Lower leverage to reduce risk');
      recommendations.push('Set tighter stop-loss');
    } else if (riskScore > 40) {
      recommendations.push('Monitor position closely');
      recommendations.push('Consider partial profit taking');
    } else {
      recommendations.push('Risk level acceptable');
    }

    if (riskFactors.volatility > 0.05) {
      recommendations.push('High volatility detected - use smaller position size');
    }

    if (riskFactors.leverage > 20) {
      recommendations.push('High leverage - ensure stop-loss is set');
    }

    return recommendations;
  }

  // Sentiment Analysis (simplified)
  analyzeSentiment(newsData, socialData) {
    const sentiment = {
      score: 0, // -100 to +100
      confidence: 0,
      sources: {
        news: 0,
        social: 0
      }
    };

    // Simple keyword-based sentiment analysis
    const positiveWords = ['bullish', 'moon', 'pump', 'surge', 'rally', 'breakout', 'strong', 'growth'];
    const negativeWords = ['bearish', 'dump', 'crash', 'fall', 'decline', 'weak', 'fear', 'panic'];

    if (newsData && newsData.length > 0) {
      let newsScore = 0;
      newsData.forEach(article => {
        const text = (article.title + ' ' + article.content).toLowerCase();
        positiveWords.forEach(word => {
          if (text.includes(word)) newsScore += 1;
        });
        negativeWords.forEach(word => {
          if (text.includes(word)) newsScore -= 1;
        });
      });
      sentiment.sources.news = newsScore;
    }

    if (socialData && socialData.length > 0) {
      let socialScore = 0;
      socialData.forEach(post => {
        const text = post.content.toLowerCase();
        positiveWords.forEach(word => {
          if (text.includes(word)) socialScore += 0.5;
        });
        negativeWords.forEach(word => {
          if (text.includes(word)) socialScore -= 0.5;
        });
      });
      sentiment.sources.social = socialScore;
    }

    sentiment.score = (sentiment.sources.news + sentiment.sources.social) * 10;
    sentiment.confidence = Math.min(Math.abs(sentiment.score) / 10, 1);

    return sentiment;
  }

  // Portfolio Optimization
  optimizePortfolio(trades, totalValue, riskTolerance = 'MEDIUM') {
    const optimization = {
      recommendedAllocation: {},
      riskLevel: riskTolerance,
      diversification: 0,
      expectedReturn: 0,
      maxDrawdown: 0
    };

    // Calculate current allocation
    const allocation = {};
    let totalAllocated = 0;

    trades.forEach(trade => {
      if (trade.status === 'ACTIVE') {
        const pair = trade.pair;
        const value = trade.amount * trade.leverage;
        
        if (!allocation[pair]) allocation[pair] = 0;
        allocation[pair] += value;
        totalAllocated += value;
      }
    });

    // Normalize allocation percentages
    Object.keys(allocation).forEach(pair => {
      allocation[pair] = totalAllocated > 0 ? (allocation[pair] / totalAllocated) * 100 : 0;
    });

    // Calculate diversification score
    const pairs = Object.keys(allocation);
    optimization.diversification = pairs.length > 0 ? Math.min(pairs.length * 10, 100) : 0;

    // Generate recommendations based on risk tolerance
    const maxPositionSize = riskTolerance === 'LOW' ? 10 : riskTolerance === 'MEDIUM' ? 20 : 30;
    
    optimization.recommendedAllocation = {};
    pairs.forEach(pair => {
      const currentAllocation = allocation[pair];
      if (currentAllocation > maxPositionSize) {
        optimization.recommendedAllocation[pair] = {
          current: currentAllocation,
          recommended: maxPositionSize,
          action: 'REDUCE'
        };
      } else if (currentAllocation < 5 && pairs.length < 10) {
        optimization.recommendedAllocation[pair] = {
          current: currentAllocation,
          recommended: 10,
          action: 'INCREASE'
        };
      }
    });

    return optimization;
  }

  // Train model with historical data
  trainModel(historicalData) {
    this.trainingData = historicalData;
    
    // Extract features from historical data
    this.features = historicalData.map(data => ({
      price: data.price,
      volume: data.volume || 0,
      timestamp: new Date(data.timestamp).getTime(),
      rsi: data.rsi || 50,
      macd: data.macd || 0,
      volatility: data.volatility || 0
    }));

    console.log(`🤖 Model trained with ${this.features.length} data points`);
    return true;
  }

  // Generate AI-powered trade suggestions
  generateAISuggestions(marketData, portfolio) {
    const suggestions = [];

    if (!marketData || !marketData.prices || marketData.prices.length === 0) {
      return suggestions;
    }

    const prices = marketData.prices;
    const currentPrice = prices[prices.length - 1];

    // Price prediction
    const predictedPrice = this.predictPrice(prices);
    if (predictedPrice) {
      const priceChange = (predictedPrice - currentPrice) / currentPrice;
      
      if (Math.abs(priceChange) > 0.02) { // 2% threshold
        suggestions.push({
          type: 'PRICE_PREDICTION',
          pair: marketData.pair || 'BTC',
          action: priceChange > 0 ? 'BUY' : 'SELL',
          confidence: Math.min(Math.abs(priceChange) * 10, 0.9),
          reason: `AI predicts ${priceChange > 0 ? 'price increase' : 'price decrease'} of ${(Math.abs(priceChange) * 100).toFixed(2)}%`,
          entryPrice: currentPrice,
          targetPrice: predictedPrice,
          stopLoss: currentPrice * (priceChange > 0 ? 0.95 : 1.05),
          riskLevel: 'MEDIUM',
          timestamp: new Date().toISOString()
        });
      }
    }

    // SMA Crossover signals
    const smaSignal = this.smaCrossoverStrategy(prices);
    if (smaSignal !== 'HOLD') {
      suggestions.push({
        type: 'SMA_CROSSOVER',
        pair: marketData.pair || 'BTC',
        action: smaSignal,
        confidence: 0.7,
        reason: `SMA crossover indicates ${smaSignal === 'BUY' ? 'bullish' : 'bearish'} trend`,
        entryPrice: currentPrice,
        riskLevel: 'LOW',
        timestamp: new Date().toISOString()
      });
    }

    return suggestions;
  }
}

